import { useState } from 'react';
import { useMemo } from 'react';
import {
  PhraseValue,
  useGetPhraseData,
  usePhraseOnChat,
  useSendPhrase,
} from 'hooks/business/usePhraseOnChat';
import { scheduleIdle } from 'utils/scheduleIdle';
import { useMemoizedFn, useMount } from 'ahooks';

/**
 * 消息编辑器快捷短语
 * 单一职责：短语数据、筛选、填充到输入框、发送短语；不包含 Popover 的 JSX，仅数据与回调
 */
export function useMessageEditorPhrase(
  channel: Conversation.Conversation['channel'],
  region: Conversation.Conversation['region'],
  storeId: Conversation.Conversation['storeId'],
  conversationId: string,
  setInput: (conversationId: string, value: string) => void,
  onSend?: () => void,
  onMercadoChatView = false,
  claimId?: string,
) {
  const sendPhraseV2 = useSendPhrase({ onSend }, onMercadoChatView, claimId);
  const { runAsync: getPhrase } = useGetPhraseData();
  const {
    phraseGroups,
    phraseList,
    phrasesByGroup,
    phraseFilter,
    isRelateRegion,
    updatePhraseFilter,
  } = usePhraseOnChat(channel, region, storeId);

  const [phraseSelected, setPhraseSelected] = useState<PhraseValue | undefined>();

  useMount(() => {
    scheduleIdle(() => {
      getPhrase().catch(() => {});
    });
  });

  /** 填充快捷短语到输入框（调用方可在其后执行 focus） */
  const handleFillPhrases = useMemoizedFn((text: string) => {
    setPhraseSelected(undefined);
    setInput(conversationId, text);
  });

  /** 被动联想 Popover 仅需的数据与回调，不含 children */
  const passivePopoverData = useMemo(
    () => ({
      phraseList,
      isRelateRegion,
      region,
      onPhraseSelected: setPhraseSelected,
      onPressEnter: (data?: PhraseValue) => {
        if (data) {
          setPhraseSelected(undefined);
          setInput(conversationId, '');
          sendPhraseV2(data);
        }
      },
      onFillPhraseText: handleFillPhrases,
    }),
    [phraseList, isRelateRegion, region, conversationId, setInput, sendPhraseV2, handleFillPhrases],
  );

  return {
    phraseGroups,
    phraseList,
    phrasesByGroup,
    phraseFilter,
    phraseSelected,
    updatePhraseFilter,
    handleFillPhrases,
    sendPhraseV2,
    setPhraseSelected,
    passivePopoverData,
  };
}
